import React, { useState } from "react";
import Helmet from "react-helmet";
import { formatCurrency } from "@wrstudios/utils";
import { Modal } from "@wrstudios/components";
import { getAverageAdjustedPricePerAcre } from "../../../utils/listings";
import Adjustment from "../../common/Adjustment";
import Wistia from "../../common/Wistia";
import { useLive } from "../LiveProvider";
import IconInfo from "../icons/IconInfo";
import LiveHeader from "../common/LiveHeader";
import LiveListingPreview, {
  useListingPreviewResizer
} from "../listing/LiveListingPreview";
import LiveListings from "./LiveListings";
import LivePricePerAcreChart from "./LiveAveragePricePerAcreChart";
import {
  Container,
  Sidebar,
  Content,
  TitleContainer,
  Title,
  SubTitle,
  Info,
  Description,
  Value,
  ModalContent,
  ModalText
} from "./styled/live-average-price-per-sqft";

function LiveAveragePricePerAcre() {
  const { visibleListings } = useLive();
  const { headerRef, mobileHeaderRef, previewRef, headerDetails } =
    useListingPreviewResizer();
  const [previewListingId, setPreviewListingId] = useState(null);
  const [hoverListingId, setHoverListingId] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const soldListings = visibleListings.filter((listing) => listing.soldPrice);
  const averageAdjustedSoldPricePerAcre =
    getAverageAdjustedPricePerAcre(soldListings);

  return (
    <>
      <Helmet>
        <title>Average Price Per Acre</title>
      </Helmet>
      <LiveHeader headerRef={headerRef} mobileHeaderRef={mobileHeaderRef}>
        Average Price/Acre
      </LiveHeader>
      <Container>
        <Sidebar>
          <LiveListings
            previewListingId={previewListingId}
            setPreviewListingId={setPreviewListingId}
            setHoverListingId={setHoverListingId}
            listingFormat={(listing) => (
              <>
                {listing.pricePerAcre ? (
                  <Adjustment
                    value={listing.pricePerAcre}
                    adjustedValue={listing.adjustedPricePerAcre}
                    shouldDim={false}
                  />
                ) : (
                  "$---"
                )}{" "}
                / acre
              </>
            )}
          />
        </Sidebar>
        <Content>
          <TitleContainer>
            <Title className="font-primary page-title">
              {averageAdjustedSoldPricePerAcre
                ? formatCurrency(averageAdjustedSoldPricePerAcre)
                : "$---"}
              <SubTitle className="page-sub-title"> / Acre</SubTitle>
            </Title>
            <Info onClick={() => setShowInfoModal(true)}>
              <IconInfo />
            </Info>
          </TitleContainer>
          {!!averageAdjustedSoldPricePerAcre && (
            <Description className="page-description">
              Comparable land sold for an average of{" "}
              <Value className="page-description-value">
                {averageAdjustedSoldPricePerAcre
                  ? `${formatCurrency(averageAdjustedSoldPricePerAcre)}`
                  : "N/A"}
              </Value>{" "}
              / acre.
            </Description>
          )}
          {!averageAdjustedSoldPricePerAcre && (
            <Description className="page-description">
              To view the average price/acre of sold listings, please add sold
              comparables to this report.
            </Description>
          )}
          <LivePricePerAcreChart
            hoverListingId={hoverListingId}
            previewListingId={previewListingId}
            setPreviewListingId={setPreviewListingId}
          />
        </Content>
        {previewListingId && (
          <LiveListingPreview
            ref={previewRef}
            style={{ top: headerDetails.bottom }}
            listingRecordId={previewListingId}
            showInModal={true}
            onClose={() => setPreviewListingId(null)}
          />
        )}
      </Container>
      {showInfoModal && (
        <Modal onClose={() => setShowInfoModal(false)}>
          <ModalContent>
            <Modal.Header className="font-primary modal-header">
              Price/Acre Scatter Chart
              <Modal.Close />
            </Modal.Header>
            <Modal.Body>
              <ModalText className="modal-text">
                The price/acre. scatter chart is a way to visualize the trend
                between the price and acreage of the home. A trendline will be
                drawn to show the correlation between price and acreage accross
                the sold listings. Watch this quick video clip on how to
                interact with this page.
              </ModalText>
              <Wistia videoId="xqe2n7zoqn" autoplay={false} />
            </Modal.Body>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}

export default LiveAveragePricePerAcre;
